import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createNewCourse } from "../../apis/CourseApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { useTheme } from "../Theme/ThemeContext";
import SeoMeta from "../SeoMeta";

export default function AddCourse() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const { successToast, errorToast } = useToastHelpers();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isPremium: false,
    orderNo: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Course title is required";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Course description is required";
    }
    if (!formData.orderNo || formData.orderNo <= 0) {
      newErrors.orderNo = "Please enter a valid order number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const courseData = {
        title: formData.title,
        description: formData.description,
        isPremium: formData.isPremium,
        orderId: parseInt(formData.orderNo),
        createdBy: user?.userId,
        updatedBy: user?.userId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await createNewCourse(courseData, user?.token);
      successToast("Course created successfully!");
      navigate("/courses");
    } catch (err) {
      console.error("Error creating course:", err);
      errorToast("Failed to create course. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoMeta
        title="Add Course - Code Arena Admin"
        description="Create a new course on Code Arena"
        url="https://www.code-arena.in/courses/new"
        image="/logo192.png"
      />

      <div className="ca-container max-w-3xl py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/courses")}
          className="flex items-center gap-2 font-medium mb-6 transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <FaArrowLeft /> Back to Courses
        </button>

        {/* Form Card */}
        <div className="ca-card overflow-hidden">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.colors.gradient} px-4 sm:px-8 py-5 sm:py-6`}>
            <h1 className="text-3xl font-bold text-white">Create New Course</h1>
            <p className="text-blue-100 mt-2">
              Fill in the details to create a new course
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
            {/* Course Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Course Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., React Fundamentals"
                className={`ca-input py-3 ${
                  errors.title
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.title && (
                <p className="text-red-600 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Course Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Course Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this course covers..."
                rows="5"
                className={`ca-input py-3 ${
                  errors.description
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.description && (
                <p className="text-red-600 text-sm mt-1">{errors.description}</p>
              )}
            </div>

            {/* Order Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Order Number *
              </label>
              <input
                type="number"
                name="orderNo"
                value={formData.orderNo}
                onChange={handleInputChange}
                placeholder="e.g., 1"
                min="1"
                className={`ca-input py-3 ${
                  errors.orderNo
                    ? "border-red-500 focus:border-red-500 focus:ring-red-600/20"
                    : ""
                }`}
              />
              {errors.orderNo && (
                <p className="text-red-600 text-sm mt-1">{errors.orderNo}</p>
              )}
            </div>

            {/* Is Premium Toggle */}
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                name="isPremium"
                id="isPremium"
                checked={formData.isPremium}
                onChange={handleInputChange}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="isPremium" className="flex-1 cursor-pointer">
                <p className="font-semibold text-gray-900">Premium Course</p>
                <p className="text-sm text-gray-600">
                  Mark this course as premium if it requires payment or special access
                </p>
              </label>
              {formData.isPremium && (
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                  Premium
                </span>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate("/courses")}
                className="ca-btn-secondary flex-1 px-6 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="ca-btn-primary flex-1 px-6 py-3"
              >
                {isSubmitting ? "Creating..." : "Create Course"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
